<template>
  <div
    v-style="gradient"
    class="pointer-events-none absolute w-full"
    :class="gradientPositionClasses"
  />
</template>

<script lang="ts" setup>
import type { Responsive } from '#types/common'
import type { GradientStyle } from '#types/styles'

const {
  gradientTone = 'transparent',
  gradientBreakpoints = { sm: true },
  gradientStyle,
  gradientGrid,
  textAreaOnlyExtent,
  position: positionProp,
  alignment: alignmentProp,
} = defineProps<{
  position: GradientPosition | Responsive<GradientPosition>
  alignment?: GradientAlignment | Responsive<GradientAlignment>
  textAreaOnlyExtent?: Responsive<string>
} & GradientStyle>()

const { breakpoints } = useAppConfig().ds
const { enableGridGradient = false } = useAppConfig().components.cms.gradient ?? {}

const position = computed(() =>
  isObject(positionProp) ? positionProp : { sm: positionProp },
)
const alignment = computed(() =>
  isObject(alignmentProp) ? alignmentProp : { sm: alignmentProp },
)
// For `text-area-only`, callers pass the measured text-container height per breakpoint so the
// gradient only covers the text block instead of the full media area. This must stay reactive:
// measurements arrive after mount via ResizeObserver, after the initial 33%/67% fallback.
const resolvedGradientTone = computed(() => resolveGradientTone(gradientTone))
const normalizedGradientBreakpoints = computed<Responsive<boolean>>(() => (
  gradientBreakpoints
  && typeof gradientBreakpoints === 'object'
  && !Array.isArray(gradientBreakpoints)
)
  ? gradientBreakpoints
  : { sm: true })

const transparentGradientTone = computed(() => getTransparentGradientTone(resolvedGradientTone.value))

const isTextAreaOnly = computed(() => gradientStyle === 'text-area-only')
const isGridGradient = computed(() =>
  isGridGradientEnabled(enableGridGradient, gradientStyle, gradientGrid)
)
// Invalid / unsupported `grid-gradient` falls back to off.
const isOff = computed(() =>
  gradientStyle === 'off' || (gradientStyle === 'grid-gradient' && !isGridGradient.value)
)

const placementBreakpoints = ['sm', 'md'] as const
const allBreakpoints = Object.keys(breakpoints) as (keyof Responsive)[]

const responsivePrefix = {
  sm: '<md:',
  md: 'md:',
  lg: 'lg:',
} as const satisfies Record<keyof Responsive, string>

const anchoredPositionClass = {
  top: { sm: '<md:top-0', md: 'md:top-0' },
  bottom: { sm: '<md:bottom-0', md: 'md:bottom-0' },
  center: { sm: '<md:top-1/2 <md:-translate-y-1/2', md: 'md:top-1/2 md:-translate-y-1/2' },
} as const

const placementClass = (
  breakpoint: keyof Responsive,
  edge: 'top' | 'bottom' = 'top'
) => {
  const prefix = responsivePrefix[breakpoint]
  return edge === 'top'
    ? `${prefix}top-0 ${prefix}left-0`
    : `${prefix}bottom-0 ${prefix}left-0`
}

const bandGradient = (pos?: GradientPosition, ali?: GradientAlignment) => {
  const direction = pos === 'center'
    ? (ali === 'right' ? 'right' : 'left')
    : pos
  return `linear-gradient(to ${direction}, ${transparentGradientTone.value}, ${resolvedGradientTone.value})`
}

const gridBackground = computed(() =>
  isGridGradient.value && isValidGradientGrid(gradientGrid)
    ? getGridGradientBackground(gradientGrid, resolvedGradientTone.value, transparentGradientTone.value)
    : undefined
)

const getPositionClass = (breakpoint: (typeof placementBreakpoints)[number]) => {
  const pos = getValueForBreakpoint(breakpoint, position.value)
  if (!pos)
    return undefined

  if (isTextAreaOnly.value) {
    const measuredExtent = textAreaOnlyExtent && getValueForBreakpoint(breakpoint, textAreaOnlyExtent)
    if (!measuredExtent)
      return undefined
    return placementClass(breakpoint, getTextAreaOnlyPosition(pos))
  }

  return anchoredPositionClass[pos][breakpoint]
}

const gradientPositionClasses = computed(() =>
  isGridGradient.value
    ? placementBreakpoints.map((breakpoint) => placementClass(breakpoint))
    : placementBreakpoints.map(getPositionClass).filter(Boolean)
)

const heightAt = (breakpoint: keyof Responsive) => {
  if (isOff.value)
    return '0%'
  if (isGridGradient.value)
    return '100%'

  const measuredExtent = textAreaOnlyExtent && getValueForBreakpoint(breakpoint, textAreaOnlyExtent)
  if (isTextAreaOnly.value)
    return measuredExtent || '0px'

  return getGradientHeight(gradientStyle)
}

const isVisibleAt = (breakpoint: keyof Responsive, enabled: boolean) => {
  if (isOff.value || !enabled)
    return false
  if (isGridGradient.value)
    return true
  if (!getValueForBreakpoint(breakpoint, position.value))
    return false
  if (isTextAreaOnly.value && !(textAreaOnlyExtent && getValueForBreakpoint(breakpoint, textAreaOnlyExtent)))
    return false
  return true
}

const backgroundAt = (breakpoint: keyof Responsive) => {
  if (gridBackground.value)
    return gridBackground.value

  const pos = getValueForBreakpoint(breakpoint, position.value)
  return bandGradient(
    isTextAreaOnly.value ? getTextAreaOnlyPosition(pos) : pos,
    getValueForBreakpoint(breakpoint, alignment.value)
  )
}

const toResponsive = (
  breakpoints: Iterable<keyof Responsive>,
  getValue: (breakpoint: keyof Responsive) => string,
): Responsive => {
  const result = { sm: getValue('sm') } as Responsive
  for (const breakpoint of breakpoints) {
    if (breakpoint === 'sm')
      continue
    result[breakpoint] = getValue(breakpoint)
  }
  return result
}

const gradient = computed(() => ({
  display: toResponsive(
    Object.keys(normalizedGradientBreakpoints.value) as (keyof Responsive)[],
    (breakpoint) => isVisibleAt(breakpoint, !!normalizedGradientBreakpoints.value[breakpoint]) ? 'block' : 'none',
  ),
  bgv: toResponsive(allBreakpoints, backgroundAt),
  h: toResponsive(allBreakpoints, heightAt),
}))
</script>
